// Per-mode, driver-side approval policy (fn-69.2, R4).
//
// Per the universal rule, the DRIVER is the single risk-routing hook —
// "auto-approve lights-out" vs "surface a yes/no card" is a config of
// OUR driver, per mode, NOT platform-level risk routing. This module is
// that pure decision.
//
// **Canonical tool-name normalizer.** The live MCP gates fire FEDERATED:
// the spike's create gate arrived as `smokeball___create_contact`, not
// `create_contact` (fn-68; memory `project_hitl_federated_mcp_toolname_mismatch`).
// So every classification runs on the BARE name = the segment after the
// last `___`. Custom tools (`review_matter`, `ask_user_question`) are
// already bare and pass through unchanged.

/**
 * The pending-approval gate the policy decides on. Structural subset of
 * the BFF approvals row (`transport.ts`'s `HeadlessApprovalRow`) — only
 * the fields the decision needs.
 */
export interface PolicyGate {
  readonly tool_name: string;
  readonly ack_kind:
    | 'tool_confirmation'
    | 'custom_tool_result_immediate'
    | 'custom_tool_result_deferred';
}

/**
 * Driver approval mode:
 *   - `lights-out` — unattended (headless ArchIE intake). Restricted
 *     forms auto-approve; `ask_user_question` auto-answers a default.
 *   - `attended`   — a human is watching. Restricted forms surface a
 *     yes/no card; `ask_user_question` surfaces the question.
 */
export type ApprovalMode = 'lights-out' | 'attended';

export type ApprovalDecision = 'auto-approve' | 'ask' | 'auto-answer-default';

/** Canonical elicitation tool name (matches `@sb-ai-platform/hitl`). */
export const ASK_USER_QUESTION_TOOL = 'ask_user_question';

/**
 * "Restricted form" tools — the gated writes the demo surfaces as
 * yes/no cards when attended. Classified on the BARE name, so both
 * `create_contact` and `smokeball___create_contact` match.
 *
 * `review_matter` is the matter-creator confirm-the-draft custom tool;
 * `create_contact` / `create_matter` are the MCP `always_ask` writes.
 */
export const RESTRICTED_FORM_TOOLS: ReadonlySet<string> = new Set([
  'review_matter',
  'create_contact',
  'create_matter',
]);

/**
 * The bare tool name = the segment after the LAST `___`. Federated MCP
 * tools arrive namespaced (`smokeball___create_contact`,
 * `mattertypes___find_matter_types`); custom + already-bare tools pass
 * through unchanged. Defensive against empty/garbage input.
 */
export function bareToolName(name: string): string {
  if (typeof name !== 'string' || name.length === 0) {
    return '';
  }
  const segments = name.split('___');
  return segments[segments.length - 1] ?? '';
}

/**
 * The per-mode approval decision, classified on the bare tool name.
 *
 *   - `ask_user_question` → `auto-answer-default` (lights-out) / `ask`
 *     (attended).
 *   - restricted forms (`review_matter`, `create_*`, federated or bare)
 *     → `auto-approve` (lights-out) / `ask` (attended).
 *   - ANYTHING else that somehow fires a gate → `ask` (fail-closed). We
 *     never blind-approve an unknown gated tool, even lights-out. Reads
 *     and dedupe tools (`analyze_document`, `find_matter_types`,
 *     `search_*`, `list_*`) run `always_allow` and never reach here; if
 *     one did, this fail-closed branch surfaces it rather than auto-
 *     approving.
 */
export function decide(gate: PolicyGate, mode: ApprovalMode): ApprovalDecision {
  const bare = bareToolName(gate.tool_name);
  if (bare === ASK_USER_QUESTION_TOOL) {
    return mode === 'lights-out' ? 'auto-answer-default' : 'ask';
  }
  if (RESTRICTED_FORM_TOOLS.has(bare)) {
    return mode === 'lights-out' ? 'auto-approve' : 'ask';
  }
  return 'ask';
}
